import { ArrowLeftOutlined, ArrowRightOutlined, ArrowsAltOutlined, FullscreenExitOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Popover, Space, Tag } from 'antd';
import { useEffect, useState } from 'react';
import Plot from 'react-plotly.js';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { arycolor } from '../../../assets/css/color';
import { aryballeColorPalette, defaultColorPalette, colorToTransparent } from '../../../compute/colormap';
import { checkAllRecordsExcluded, defaultPlotlyArguments, getGroupedMap } from '../../../compute/utils';
import {
  selectAggregatePeptides,
  selectBoundariesMap,
  selectColormap,
  selectColormapRef,
  selectExcludedPeptides,
  selectExcludedRecordIDs,
  selectExcludedSpots,
  selectRecords,
  selectSessionID,
  selectSubtractItemName,
  setBoundariesMap,
} from '../../../features/analysisConfig/analysisConfigSlice';
import { AryGroupEnvelope, AryRecord, ComparisonMeasuresType, ZoneSelectorType } from '../../../types/analysisTypes';
import RecordsLegend from '../../RecordsLegend';
import FullscreenGraphicModal from '../FullscreenGraphicModal';
import { fetchAuthorizedAPIEndpoint, useAuth0OrQueryAuth } from '../../../utils';
import lockAll from '../../../assets/images/lockAll.svg';

export const MultiSensogram: React.FC<{ showLegend?: boolean; isStatic?: boolean; qualityControlMode?: boolean }> = (props) => {
  const { showLegend, isStatic, qualityControlMode } = props;

  const accessToken = useAuth0OrQueryAuth();

  const aggregatePeptides = useAppSelector(selectAggregatePeptides);
  const boundariesMap = useAppSelector(selectBoundariesMap);
  const subtractItemName = useAppSelector(selectSubtractItemName);
  const excludedRecordIDs = useAppSelector(selectExcludedRecordIDs);
  const sessionID = useAppSelector(selectSessionID);
  const cmap = useAppSelector(selectColormap);
  const cmapRef = useAppSelector(selectColormapRef);
  const records = useAppSelector(selectRecords);
  const excludedSpots = useAppSelector(selectExcludedSpots);
  const excludedPeptides = useAppSelector(selectExcludedPeptides);

  const [plotlyData, setPlotlyData] = useState<Plotly.Data[]>([]);
  const [plotlyLayout, setPlotlyLayout] = useState<Partial<Plotly.Layout>>({});

  const [isVisibleModal, setIsVisibleModal] = useState<boolean>(false);

  const dispatch = useAppDispatch();

  const [groupEnvelopes, setGroupEnvelopes] = useState<AryGroupEnvelope[]>([]);
  const [error, setError] = useState<string | undefined>();
  const [effectiveCommonAnalyteLeft, setEffectiveCommonAnalyteLeft] = useState(0);
  const [effectiveCommonAnalyteRight, setEffectiveCommonAnalyteRight] = useState(0);
  const [shouldDrawCommonBoundaries, setShouldDrawCommonBoundaries] = useState(true);
  const [zoneSelectorType, setZoneSelectorType] = useState<ZoneSelectorType>(ZoneSelectorType.Analyte);
  const [_cmap, setCmap] = useState<Record<string, string> | undefined>();

  useEffect(() => {
    if (!accessToken) return;
    const { error } = checkAllRecordsExcluded(records, excludedRecordIDs);
    if (error) {
      setError(error);
      return;
    } else setError(undefined);

    fetchAuthorizedAPIEndpoint(`/group_envelopes?session_id=${sessionID}`, accessToken, {
      method: 'POST',
      body: JSON.stringify({
        sessionID,
        aggregatePeptides,
        subtractItemName,
        excludedRecordIDs,
        excludedPeptides,
        excludedSpots,
      }),
    })
      .then((resp) => {
        if (resp.ok) {
          return resp.json();
        } else {
          throw resp.json();
        }
      })
      .then((receivedGroupEnvelopes: AryGroupEnvelope[]) => {
        setGroupEnvelopes(receivedGroupEnvelopes);
        setError('');
      })
      .catch((e) => {
        Promise.resolve(e).then((resp: { Reason: string }) => {
          setError(resp.Reason);
        });
      });
  }, [accessToken, sessionID, aggregatePeptides, subtractItemName, excludedRecordIDs, excludedSpots, excludedPeptides]);

  useEffect(() => {
    if (qualityControlMode && cmapRef !== undefined) {
      setCmap({ ...cmapRef[ComparisonMeasuresType.Reference], ...cmapRef[ComparisonMeasuresType.Test], ...cmapRef[ComparisonMeasuresType.Other] });
    } else if (cmap !== undefined) {
      setCmap(cmap);
    }
  }, [cmap, cmapRef, qualityControlMode]);

  useEffect(() => {
    if (records === undefined || groupEnvelopes === undefined || groupEnvelopes === null || _cmap === undefined) return;

    let effectiveLabels: string[] = [];
    let effectiveRecords: AryRecord[] = [];

    if (groupEnvelopes.length === 0) return;

    records.forEach((record) => {
      if (!excludedRecordIDs.includes(record.ID)) {
        effectiveRecords.push(record);
        effectiveLabels.push(record.ItemName);
      }
    });
    if (effectiveRecords.length === 0) return;

    const groupedEffectiveRecords: Record<string, AryRecord[]> = getGroupedMap(effectiveLabels, effectiveRecords);

    var commonBoundariesMap: Record<string, Set<string>> = {};
    effectiveRecords.forEach((record) => {
      var boundaries: number[] = [0, 0, 1e6, -1e6];
      boundaries[2] = Math.min(boundaries[2], boundariesMap[record.ID][2]);
      boundaries[3] = Math.max(boundaries[3], boundariesMap[record.ID][3]);
      let boundariesKey = `${boundaries[2]}_${boundaries[3]}`;

      if (commonBoundariesMap[boundariesKey] === undefined) {
        commonBoundariesMap[boundariesKey] = new Set<string>();
      }
      commonBoundariesMap[boundariesKey].add(record.ItemName);
    });

    let commonBoundariesArr = Object.entries(commonBoundariesMap);
    commonBoundariesArr.sort(([keyA, valueA], [keyB, valueB]) => valueB.size - valueA.size);

    // Should only draw "common" zone if at least 2 groups have common boundaries
    if (commonBoundariesArr[0][1].size < 2) {
      setShouldDrawCommonBoundaries(false);
    }

    let mostCommonBoundariesStr = commonBoundariesArr[0][0];
    let mostCommonBoundaries = mostCommonBoundariesStr.split('_').map((e) => parseInt(e));
    mostCommonBoundaries.splice(0, 0, ...[0, 0]);

    if (mostCommonBoundaries[2] !== effectiveCommonAnalyteLeft) {
      setEffectiveCommonAnalyteLeft(mostCommonBoundaries[2]);
    }
    if (mostCommonBoundaries[3] !== effectiveCommonAnalyteRight) {
      setEffectiveCommonAnalyteRight(mostCommonBoundaries[3]);
    }

    // Compute Plotly Data and Layout

    const _plotlyLayout: Partial<Plotly.Layout> = { ...defaultPlotlyArguments.layout };
    _plotlyLayout.dragmode = 'select';
    _plotlyLayout.yaxis = { title: { text: 'Intensity, rad' }, automargin: true };
    _plotlyLayout.xaxis = { title: { text: 'Time, sec' }, automargin: true };
    _plotlyLayout.annotations = [];
    if (showLegend) {
      _plotlyLayout.showlegend = true;
      _plotlyLayout.legend = {
        orientation: 'v',
      };
    }

    let _plotlyData: Plotly.Data[] = [];

    var apexMax = 0;
    var apexMin = 0;
    // var commonBoundariesMap: Record<string, Set<string>> = {}

    groupEnvelopes.forEach((groupEnvelope) => {
      var frameNb = groupEnvelope.MedianDataSeries.length;
      for (let frameIdx = 0; frameIdx < frameNb; frameIdx++) {
        let _avg = groupEnvelope.MedianDataSeries[frameIdx];
        apexMax = Math.max(apexMax, _avg);
        apexMin = Math.min(apexMin, _avg);
      }
    });

    // Set it to true once common boundaries are drawn once. Do not re-draw them for every group
    var isCommonBoundariesDrawn = false;

    Object.entries(groupedEffectiveRecords).forEach(([groupName, groupRecords]) => {
      let boundaryLeftDefault = 1e6;
      let boundaryRightDefault = -1e6;

      var boundaries: number[] = [0, 0, boundaryLeftDefault, boundaryRightDefault];
      var isLeftBoundaryHeterogeneous = false;
      var isRightBoundaryHeterogeneous = false;

      var groupEnvelope: AryGroupEnvelope | undefined = undefined;
      for (let i = 0; i < groupEnvelopes.length; i++) {
        if (groupName === groupEnvelopes[i].GroupName) {
          groupEnvelope = groupEnvelopes[i];
        }
      }
      if (groupEnvelope === undefined) {
        return;
      }

      groupRecords.forEach((record) => {
        if (boundaries[2] !== boundariesMap[record.ID][2]) {
          if (boundaries[2] !== boundaryLeftDefault) {
            isLeftBoundaryHeterogeneous = true;
          }
          boundaries[2] = Math.min(boundaries[2], boundariesMap[record.ID][2]);
        }
        if (boundaries[3] !== boundariesMap[record.ID][3]) {
          if (boundaries[3] !== boundaryRightDefault) {
            isRightBoundaryHeterogeneous = true;
          }
          boundaries[3] = Math.max(boundaries[3], boundariesMap[record.ID][3]);
        }
      });

      var aggGroupAvgSeries: number[] = groupEnvelope.MedianDataSeries;

      _plotlyData.push({
        type: 'scatter',
        name: `${groupName}`,
        legendgroup: groupName,
        x: aggGroupAvgSeries.map((_, i) => i / 3).map((e) => e.toFixed(1)),
        y: aggGroupAvgSeries.map((e) => e.toFixed(3)),
        line: {
          color: qualityControlMode ? _cmap[groupName] : defaultColorPalette[_cmap[groupName]],
          width: 3,
        },
      });

      let anColorLine = qualityControlMode ? _cmap[groupName] : defaultColorPalette[_cmap[groupName]];
      let anColorFill = colorToTransparent(anColorLine, 40);
      let marker: Partial<Plotly.ScatterMarker> = {};
      let traceName = groupName;

      let analyteLeft = boundaries[2] / 3;
      let analyteRight = (boundaries[3] - 1) / 3;

      // Set common boundaries
      if (shouldDrawCommonBoundaries && boundaries[2] === mostCommonBoundaries[2] && boundaries[3] === mostCommonBoundaries[3]) {
        if (isCommonBoundariesDrawn === true) {
          return;
        }
        anColorLine = aryballeColorPalette.gray;
        anColorFill = colorToTransparent(aryballeColorPalette.gray, 40);

        marker = {
          symbol: 'square',
          size: 8,
        };

        traceName = 'Common analyte';
        isCommonBoundariesDrawn = true;
      }

      _plotlyData.push({
        type: 'scatter',
        x: [analyteLeft, analyteLeft],
        y: [apexMin, apexMax],
        name: traceName,
        legendgroup: groupName,
        showlegend: false,
        line: {
          color: anColorLine,
        },
        marker: marker,
      });
      _plotlyData.push({
        type: 'scatter',
        x: [analyteRight, analyteRight],
        y: [apexMin, apexMax],
        name: traceName,
        legendgroup: groupName,
        showlegend: false,
        fill: 'tonextx',
        line: {
          color: anColorLine,
        },
        fillcolor: anColorFill,
        marker: marker,
      });

      if (isLeftBoundaryHeterogeneous) {
        _plotlyLayout.annotations?.push({
          x: analyteLeft,
          y: apexMax,
          ax: -10,
          ay: 0,
          showarrow: true,
          text: '⚠️',
          font: {
            size: 16,
          },
          hovertext: 'Some record(s) have independently set boundaries',
        });
      }

      if (isRightBoundaryHeterogeneous) {
        _plotlyLayout.annotations?.push({
          x: analyteRight,
          y: apexMax,
          ax: 10,
          ay: 0,
          showarrow: true,
          text: '⚠️',
          font: {
            size: 16,
          },
          hovertext: 'Some record(s) have independently set boundaries',
        });
      }
    });

    setPlotlyData(_plotlyData);
    setPlotlyLayout(_plotlyLayout);
  }, [groupEnvelopes, boundariesMap, records]); // As in the main fetch but with the boundaries

  const mutateBoundaries = (left: number, right: number) => {
    if (records === undefined) {
      return;
    }
    let _boundariesMap = { ...boundariesMap };
    records.forEach((r) => {
      let _boundaries = _boundariesMap[r.ID].map((e) => e);
      if (zoneSelectorType === +ZoneSelectorType.Baseline) {
        _boundaries[0] = left;
        _boundaries[1] = right;
      } else {
        _boundaries[2] = left;
        _boundaries[3] = right;
      }
      _boundariesMap[r.ID] = _boundaries;
    });
    dispatch(setBoundariesMap(_boundariesMap));
  };

  if (error) {
    return (
      <div>
        <Alert type="error" message={error} />
      </div>
    );
  }

  let plotlyConfig: Partial<Plotly.Config> = { ...defaultPlotlyArguments.config };
  plotlyConfig.displayModeBar = true;
  plotlyConfig.modeBarButtons = [['zoom2d', 'select2d'] as Plotly.ModeBarDefaultButtons[]];
  if (isStatic) {
    plotlyConfig.staticPlot = isStatic;
  }

  if (plotlyData.length === 0) {
    return null;
  }

  return (
    <>
      <div
        tabIndex={0}
        onKeyDown={(e) => {
          let n: number;
          switch (e.key) {
            case 'ArrowLeft':
              if (e.ctrlKey) {
                n = 1;
              } else {
                n = 5;
              }
              mutateBoundaries(effectiveCommonAnalyteLeft - n, effectiveCommonAnalyteRight - n);
              break;
            case 'ArrowRight':
              if (e.ctrlKey) {
                n = 1;
              } else {
                n = 5;
              }
              mutateBoundaries(effectiveCommonAnalyteLeft + n, effectiveCommonAnalyteRight + n);
              break;
            case 'Shift':
              setZoneSelectorType(ZoneSelectorType.Baseline);
              break;
          }
        }}
        onKeyUp={(e) => {
          switch (e.key) {
            case 'Shift':
              setZoneSelectorType(ZoneSelectorType.Analyte);
              break;
          }
        }}
        style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
      >
        {isStatic ? null : (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Space>
              <Popover
                style={{ display: 'flex' }}
                trigger={'hover'}
                content={
                  <div>
                    <p>
                      Press{' '}
                      <Tag>
                        <ArrowLeftOutlined />
                      </Tag>
                      or{' '}
                      <Tag>
                        <ArrowRightOutlined />
                      </Tag>
                      to translate <b>analyte zone</b> (press <Tag>Ctrl</Tag>for precise control)
                    </p>
                  </div>
                }
              >
                <FontAwesomeIcon icon="info-circle" className="clickable-icon" style={{ color: arycolor.aryBlue }} />
              </Popover>
              <Popover
                style={{ display: 'flex' }}
                trigger={'hover'}
                placement="bottom"
                content={
                  <div>
                    <p>
                      <span>
                        Same analyte zone will be applied to <b>all records</b> in the dataset
                      </span>
                    </p>
                  </div>
                }
              >
                <img
                  src={lockAll}
                  alt=""
                  style={{
                    width: 'auto',
                    marginBottom: '5px',
                  }}
                />
              </Popover>
              <Popover
                style={{ display: 'flex' }}
                trigger={'hover'}
                content={
                  <div>
                    <p>Click to enable keyboard features!</p>
                  </div>
                }
              >
                <FullscreenExitOutlined
                  style={{
                    cursor: 'pointer',
                    fontSize: '14pt',
                  }}
                />
              </Popover>
              {zoneSelectorType === ZoneSelectorType.Baseline ? (
                <Popover
                  style={{ display: 'flex' }}
                  trigger={'hover'}
                  content={
                    <div>
                      <p>
                        Selection mode: <b>baseline</b>
                        <br />
                        <b>Note:</b> baseline selection mode is ON but can only be visualized when in <b>single sensogram</b> view
                      </p>
                    </div>
                  }
                >
                  <div style={{ cursor: 'help', borderRadius: '3px', borderWidth: '1px', borderStyle: 'solid', backgroundColor: '#eee', marginBottom: '4px' }}>BL</div>
                </Popover>
              ) : null}
            </Space>
            <Space>
              <ArrowsAltOutlined className="clickable-icon" onClick={() => setIsVisibleModal(true)} />
            </Space>
          </div>
        )}
        <Plot
          divId="sensogram_scatter_plot"
          debug={true}
          data={plotlyData}
          layout={plotlyLayout}
          config={plotlyConfig}
          style={defaultPlotlyArguments.style}
          useResizeHandler={true}
          onSelected={(e) => {
            if (e.range === undefined) {
              return;
            }
            if (records === undefined) {
              return;
            }

            let range = e.range.x;
            let [startTime, endTime] = range;
            let startIdx = Math.round(startTime * 3);
            let endIdx = Math.round(endTime * 3);

            mutateBoundaries(startIdx, endIdx);
          }}
        />
        <FullscreenGraphicModal title="Dataset" visible={isVisibleModal} onCancel={() => setIsVisibleModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', marginTop: '10px' }}>
            <Plot
              divId="sensogram_scatter_plot"
              debug={true}
              data={plotlyData}
              layout={plotlyLayout}
              config={plotlyConfig}
              style={defaultPlotlyArguments.style}
              useResizeHandler={true}
              onSelected={(e) => {
                if (e.range === undefined) {
                  return;
                }
                if (records === undefined) {
                  return;
                }

                let range = e.range.x;
                let [startTime, endTime] = range;
                let startIdx = Math.round(startTime * 3);
                let endIdx = Math.round(endTime * 3);

                mutateBoundaries(startIdx, endIdx);
              }}
            />
            <RecordsLegend qualityControlMode={qualityControlMode} />
          </div>
        </FullscreenGraphicModal>
      </div>
    </>
  );
};
